<template>
  <div class="club-tournaments-view">
    <v-overlay :value="loading" :absolute="true" :dark="false" z-index="5">
      <v-progress-circular :color="clubColor" :size="50" width="4" indeterminate />
    </v-overlay>

    <p v-if="!loading && this.$store.state.clubTournaments.length === 0 && !showingArchived" class="text-center" style="margin-top: 30%">
      <v-btn :color="`#${$store.state.clubMember.club.primaryColor} !important`"
        v-if="this.$store.state.clubMember.rol !== 'MEMBER'" class=" mb-5 mt-n5" @click="addTournament">{{
          $t('btnAddFirst') }}<v-icon class="icon ml-2" color="#FFFFFF !important" small>fa-plus</v-icon>
      </v-btn>
      <br>
      {{ $t('noTournaments') }}
    </p>

    <div v-else>
      <v-container fluid>
        <v-row class="mt-3">
          <section class="all-tournaments">
            <!-- Tabs + Create row -->
            <div class="tournaments-heading">
              <div class="tournaments-heading__items" :style="{ '--club-color': clubColor }">
                <div class="tabs" :style="{ '--club-color': clubColor }">
                  <input checked type="radio" id="active-option" name="tabs" value="actives" v-model="atActiveTab" />
                  <label class="tab" for="active-option"><i class="fa-regular fa-trophy" />{{
                    $t("tabs.activeAndUpcoming") || $t("tabs.active") }}</label>
                  <input type="radio" id="finished-option" name="tabs" value="finished" v-model="atActiveTab" />
                  <label class="tab" for="finished-option"><i class="fa-regular fa-flag-checkered"></i>{{
                    $t("tabs.finished") }}</label>
                  <input type="radio" id="archived-option" name="tabs" value="archived" v-model="atActiveTab" />
                  <label class="tab" for="archived-option"><i class="fa-regular fa-archive"></i>{{
                    $t("tabs.archived") || 'Archived' }}</label>
                  <span class="glider"></span>
                </div>
                <v-btn
                  v-if="this.$store.state.clubMember.rol !== 'MEMBER'"
                  class="create-btn"
                  :style="{ backgroundColor: clubColor + ' !important' }"
                  @click="addTournament">
                  {{ $t('createNew') || 'Create' }} <v-icon small class="ml-1" color="#FFFFFF">fa-regular fa-plus</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Search bar -->
            <div class="tournaments-search" :style="{ '--club-color': clubColor }">
              <div class="search-container">
                <v-text-field v-model="searchQuery" :placeholder="$t('searchTournament')"
                  prepend-inner-icon="fa-regular fa-search" dense outlined hide-details class="search-input"
                  clearable></v-text-field>
              </div>
            </div>

            <!-- Count + Grid/List toggle row -->
            <div class="tournaments-count-row">
              <p class="competitions-count">{{ filteredTournaments.length }} {{ $t('competitionsCreated') || 'Competitions Created' }}</p>
              <div class="view-toggle" :style="{ '--club-color': clubColor }">
                <button :class="{ active: viewMode === 'grid' }" @click="viewMode = 'grid'" :title="$t('gridView') || 'Grid view'">
                  <i class="fa-regular fa-grid-2"></i>
                </button>
                <button :class="{ active: viewMode === 'list' }" @click="viewMode = 'list'" :title="$t('listView') || 'List view'">
                  <i class="fa-regular fa-list"></i>
                </button>
              </div>
            </div>

            <!-- Grid View -->
            <div v-if="viewMode === 'grid'" class="all-tournaments__cards">
              <div class="cards-container-parent" @scroll="(e) => updateSliderControlsStatus(e.target)">
                <div class="cards-container">
                  <TournamentCard
                    v-for="(tournament) in filteredTournaments"
                    :key="tournament.id"
                    :data-tournament-id="tournament.id"
                    cardType="allVariation"
                    :hideTagHeader="true"
                    :tournamentMsgState="getMessageStatus('ALL', tournament.enrolled, tournament.active, tournament.inProgress, tournament.lastUserRankingPlace, tournament.tournamentParticipanteStatus)"
                    :tournamentTitle="tournament.name"
                    :tournamentStartDate="new Date(tournament.startTime)"
                    :tournamentEndDate="new Date(tournament.endTime)"
                    :tournamentInfo="getTournamentInfo(tournament)"
                    :tournamentActive="tournament.active"
                    :tournamentInProgress="tournament.inProgress"
                    :tournamentUserEnrolled="tournament.enrolled"
                    :tournamentId="tournament.id"
                    :showArchiveOption="atActiveTab === 'finished' || atActiveTab === 'archived'"
                    :isFinished="tournament.finished"
                    :tournamentVisibility="tournament.tournamentVisibility"
                    @tournamentArchived="handleTournamentArchived"
                    @showMessage="showSnackbar"
                    @forceRefresh="handleForceRefresh">
                  </TournamentCard>
                </div>
              </div>
              <p v-show="!loading && !filteredTournaments.length"
                class="all-tournaments__cards__noTournaments-paragraph">{{
                  noTournamentsMessage }}</p>
              <div class="slider-controls" :style="{ '--club-color': clubColor }">
                <button disabled @click="(e) => scrollContainer('at', false)" class="prev-btn">
                  <i class="fa-regular fa-chevron-left"></i>
                </button>
                <button disabled @click="(e) => scrollContainer('at', true)" class="next-btn">
                  <i class="fa-regular fa-chevron-right"></i>
                </button>
              </div>
            </div>

            <!-- List View -->
            <div v-else class="all-tournaments__list">
              <table class="competitions-table" :style="{ '--club-color': clubColor }">
                <thead>
                  <tr>
                    <th>{{ $t('competitionName') || 'Competition Name' }}</th>
                    <th>{{ $t('startDate') || 'Start Date' }}</th>
                    <th v-if="showEndDateColumn">{{ $t('endDate') || 'End Date' }}</th>
                    <th class="th-center">{{ $t('tournamentInfo.participants') || 'Participants' }}</th>
                    <th class="th-center">{{ $t('tournamentInfo.prizes') || 'Prizes' }}</th>
                    <th v-if="isUserAdmin" class="th-center">{{ $t('tableActions') || 'Actions' }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tournament in paginatedTournaments" :key="tournament.id" @click="goToTournament(tournament)">
                    <td class="td-name">{{ tournament.name }}</td>
                    <td class="td-date">{{ getFormattedDate(new Date(tournament.startTime)) }}</td>
                    <td v-if="showEndDateColumn" class="td-date">{{ getFormattedDate(new Date(tournament.endTime)) }}</td>
                    <td class="td-participants">
                      {{ tournament.detailsLoaded ? tournament.participantCount : (tournament.participantCount || 0) }}
                    </td>
                    <td class="td-prizes">
                      {{ tournament.detailsLoaded ? tournament.awardCount : (tournament.awardCount || 0) }}
                    </td>
                    <td v-if="isUserAdmin" class="td-actions" @click.stop>
                      <span class="action-icon" @click="goToTournamentShare(tournament)" :title="$t('share') || 'Share'">
                        <i class="fa-regular fa-share-alt"></i>
                      </span>
                      <span v-if="tournament.finished && tournament.tournamentVisibility !== 'ARCHIVED'"
                        class="action-icon" @click="archiveTournamentPrompt(tournament)" :title="$t('archiveTournament') || 'Archive'">
                        <i class="fa-regular fa-archive"></i>
                      </span>
                      <span v-if="tournament.tournamentVisibility === 'ARCHIVED'"
                        class="action-icon" @click="unarchiveTournamentPrompt(tournament)" :title="$t('unarchiveTournament') || 'Unarchive'">
                        <i class="fa-regular fa-box-open"></i>
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!loading && !filteredTournaments.length">
                    <td :colspan="emptyColspan" class="td-empty">{{ noTournamentsMessage }}</td>
                  </tr>
                </tbody>
              </table>
              <!-- Pagination -->
              <div v-if="filteredTournaments.length > listItemsPerPage" class="list-pagination" :style="{ '--club-color': clubColor, '--club-contrast': tabContrastTextColor }">
                <div class="pagination-info">
                  {{ paginationStart }}-{{ paginationEnd }} {{ $t('of') || 'of' }} {{ filteredTournaments.length }}
                </div>
                <div class="pagination-pages">
                  <button
                    v-for="page in totalPages"
                    :key="page"
                    :class="{ active: currentPage === page }"
                    @click="currentPage = page">
                    {{ page }}
                  </button>
                </div>
                <div class="pagination-per-page">
                  <select v-model="listItemsPerPage">
                    <option :value="10">10</option>
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                  </select>
                </div>
              </div>
            </div>
          </section>
        </v-row>
      </v-container>
    </div>

    <!-- Archive/Unarchive confirmation dialog -->
    <v-dialog v-model="showArchiveDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">
          {{ archiveAction === 'archive' ? ($t('archiveTournament') || 'Archive') : ($t('unarchiveTournament') || 'Unarchive') }}
        </v-card-title>
        <v-card-text>
          {{ archiveAction === 'archive' ? ($t('confirmArchiveTournament') || 'Are you sure you want to archive this competition?') : ($t('confirmUnarchiveTournament') || 'Are you sure you want to unarchive this competition?') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showArchiveDialog = false">{{ $t('btnCancel') || 'Cancel' }}</v-btn>
          <v-btn color="primary" @click="confirmArchiveAction" :loading="archiving">
            {{ archiveAction === 'archive' ? ($t('archiveTournament') || 'Archive') : ($t('unarchiveTournament') || 'Unarchive') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for messages -->
    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>

    <Wizard
      :class="'tournaments-wizard ' + (wizardOpen ? 'three ' : '') + (!wizardOpen && fromWizard ? 'three out' : '')"
      :fromButton="true" :hideWizard="hideWizard" type="tournament"></Wizard>
    <v-overlay :dark="false" v-model="overlayUnavailableFeature">
      <UnavailableFeature :hasCloseWindowAction="true" @closeWindow="overlayUnavailableFeature = false" />
    </v-overlay>
  </div>
</template>

<script>
import Repository from '../../../repositories/tournament';
import UnavailableFeature from './UnavailableFeature';
import { mapMutations } from 'vuex';
import tournament from '../../../repositories/tournament';
import TournamentCard from "../../Tournament/components/TournamentCard.vue";
import Wizard from "../../Funding/Simulation/components/Wizard/Wizard.vue";

export default {
  name: "ClubTournaments",
  components: { Wizard, UnavailableFeature, TournamentCard },
  data() {
    return {
      overlayAddTournament: false,
      overlayUnavailableFeature: false,
      isFeatureUnavailable: true,
      featurePermissionInfo: null,
      atActiveTab: "actives",
      atFilteredTournaments: [],
      allTournaments: [],
      userTournaments: [],
      loading: false,
      searchQuery: '',
      wizardOpen: false,
      fromWizard: false,
      // New properties for lazy loading
      observer: null,
      tournamentDetailsCache: new Map(), // Cache for tournament details
      loadingTournaments: new Set(), // Track which tournaments are currently loading
      enhancedTournaments: new Map(), // Store enhanced tournament data
      
      // New properties for archived tournaments
      showingArchived: false,
      
      // Snackbar for messages
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',

      // View mode and pagination
      viewMode: 'grid',
      currentPage: 1,
      listItemsPerPage: 10,

      // Archive dialog
      showArchiveDialog: false,
      archiveAction: 'archive',
      archiveTarget: null,
      archiving: false,
    };
  },
  watch: {
    atActiveTab: async function () {
      this.loading = true;
      this.currentPage = 1;
      if (this.atActiveTab === 'archived') {
        this.showingArchived = true;
        this.atFilteredTournaments = this.getTournamentsFiltered("all", "finished", false)
          .filter(t => t.tournamentVisibility === 'ARCHIVED');
      } else {
        this.showingArchived = this.atActiveTab === 'finished' ? false : this.showingArchived;
        this.atFilteredTournaments = this.getTournamentsFiltered("all", this.atActiveTab, false);
      }

      setTimeout(() => {
        const container = document.querySelector('.club-tournaments-view .all-tournaments .cards-container-parent');
        if (container) this.updateSliderControlsStatus(container);
        this.startObservingCards();
      }, 0)
      this.loading = false;
    },
    
    viewMode(mode) {
      if (mode === 'grid') {
        this.$nextTick(() => {
          const container = document.querySelector('.club-tournaments-view .all-tournaments .cards-container-parent');
          if (container) this.updateSliderControlsStatus(container);
          this.startObservingCards();
        });
      }
    },

    // Watch filtered tournaments to observe new cards
    atFilteredTournaments: {
      handler() {
        this.$nextTick(() => {
          this.startObservingCards();
        });
      },
      deep: true
    }
  },
  computed: {
    filteredTournaments() {
      let tournaments = this.atFilteredTournaments;

      // For the archived tab, only show archived
      if (this.atActiveTab === 'archived') {
        tournaments = tournaments.filter(t => t.tournamentVisibility === 'ARCHIVED');
      } else if (this.atActiveTab === 'finished') {
        // In finished tab, filter out archived
        tournaments = tournaments.filter(t => t.tournamentVisibility !== 'ARCHIVED');
      } else {
        // Active tab - filter out archived
        tournaments = tournaments.filter(t => t.tournamentVisibility !== 'ARCHIVED');
      }

      if (!this.searchQuery) return tournaments;

      const query = this.searchQuery.toLowerCase().trim();
      return tournaments.filter(tournament =>
        tournament.name.toLowerCase().includes(query)
      );
    },
    
    clubColor() {
      return `#${this.$store.state.clubMember.club.primaryColor}`;
    },
    
    isUserAdmin() {
      return this.$store.state.clubMember && 
             (this.$store.state.clubMember.rol === 'ADMIN' || this.$store.state.clubMember.rol === 'FOUNDER');
    },
    
    paginatedTournaments() {
      const start = (this.currentPage - 1) * this.listItemsPerPage;
      return this.filteredTournaments.slice(start, start + this.listItemsPerPage);
    },
    totalPages() {
      return Math.ceil(this.filteredTournaments.length / this.listItemsPerPage);
    },
    paginationStart() {
      return ((this.currentPage - 1) * this.listItemsPerPage) + 1;
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.listItemsPerPage, this.filteredTournaments.length);
    },
    showEndDateColumn() {
      return this.atActiveTab === 'finished' || this.atActiveTab === 'archived';
    },
    emptyColspan() {
      let cols = 4; // name, start date, participants, prizes
      if (this.showEndDateColumn) cols++;
      if (this.isUserAdmin) cols++;
      return cols;
    },
    noTournamentsMessage() {
      if (this.atActiveTab === 'finished') return this.$t('noFinishedTournaments');
      if (this.atActiveTab === 'archived') return this.$t('noArchivedTournaments');
      return this.$t('noActiveTournaments');
    },
    tabContrastTextColor() {
      const hexColor = this.$store.state.clubMember.club.primaryColor;
      
      // Convert hex to RGB
      const r = parseInt(hexColor.substr(0, 2), 16);
      const g = parseInt(hexColor.substr(2, 2), 16);
      const b = parseInt(hexColor.substr(4, 2), 16);
      
      // Calculate YIQ contrast ratio
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      
      // Return black for light backgrounds, white for dark backgrounds
      return yiq >= 128 ? '#000000' : '#ffffff';
    }
  },
  async mounted() {
    await this.init();
    this.setupIntersectionObserver();
    
    // Set CSS custom property for tab text color
    this.$nextTick(() => {
      document.documentElement.style.setProperty('--tab-active-text-color', this.tabContrastTextColor);
    });
  },
  beforeDestroy() {
    // Clean up intersection observer
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    ...mapMutations(['setClubTournaments']),
    
    // New method to setup intersection observer
    setupIntersectionObserver() {
      const options = {
        root: null, // Use viewport as root
        rootMargin: '50px', // Start loading 50px before the element enters viewport
        threshold: 0.1 // Trigger when 10% of the element is visible
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const tournamentId = parseInt(entry.target.dataset.tournamentId);
            if (tournamentId && !this.tournamentDetailsCache.has(tournamentId) && !this.loadingTournaments.has(tournamentId)) {
              this.loadTournamentDetails(tournamentId);
            }
          }
        });
      }, options);
    },

    // New method to observe tournament cards
    observeTournamentCard(tournamentId) {
      this.$nextTick(() => {
        const cardElement = document.querySelector(`[data-tournament-id="${tournamentId}"]`);
        if (cardElement && this.observer) {
          this.observer.observe(cardElement);
        }
      });
    },

    // New method to load tournament details via overview API
    async loadTournamentDetails(tournamentId) {
      if (this.loadingTournaments.has(tournamentId) || this.tournamentDetailsCache.has(tournamentId)) {
        return;
      }

      this.loadingTournaments.add(tournamentId);

      try {
        // Use admin endpoint for admins, regular endpoint for normal users
        const response = this.isUserAdmin
          ? await Repository.getTournamentOverviewAdmin(tournamentId)
          : await Repository.getTournamentOverview(tournamentId);
        
        if (response.status < 400) {
          const tournamentDetails = response.data;
          
          // Cache the details
          this.tournamentDetailsCache.set(tournamentId, tournamentDetails);
          
          // Update the tournament data with enhanced information
          this.updateTournamentWithDetails(tournamentId, tournamentDetails);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Failed to load tournament details for ID ${tournamentId}:`, error);
      } finally {
        this.loadingTournaments.delete(tournamentId);
      }
    },

    // Enhanced method to update tournament with detailed information
    updateTournamentWithDetails(tournamentId, details) {
      // Find and update the tournament in all relevant arrays
      const updateTournament = (tournament) => {
        if (tournament.id === tournamentId) {
          // Enhance tournament with detailed information
          tournament.participantCount = details.participants ? details.participants.length : 0;
          tournament.awardCount = details.awards ? details.awards.length : 0;
          
          // Update visibility from tournamentSettings if available (this is the key fix)
          if (details.tournamentSettings && details.tournamentSettings.tournamentVisibility) {
            tournament.tournamentVisibility = details.tournamentSettings.tournamentVisibility;
          }
          
          // Store additional details that might be useful
          tournament.detailsLoaded = true;
          tournament.tournamentDetails = details;
          
          return true;
        }
        return false;
      };

      // Update in all tournament arrays
      this.allTournaments.forEach(updateTournament);
      this.atFilteredTournaments.forEach(updateTournament);
      
      // Update in the store as well to maintain consistency
      if (this.$store.state.clubTournaments) {
        this.$store.state.clubTournaments.forEach(updateTournament);
      }
      
      // Force reactivity update
      this.$forceUpdate();
    },

    // Enhanced method to get tournament info with loading states
    getTournamentInfo(tournament) {
      const isLoading = this.loadingTournaments.has(tournament.id);
      const hasDetails = tournament.detailsLoaded;
      
      return [
        { 
          icon: 'fa-regular fa-calendar', 
          text: this.getFormattedDate(new Date(tournament.startTime)) 
        },
        { 
          icon: 'fa-regular fa-user', 
          text: isLoading ? 
            this.$t('loading') : 
            `${hasDetails ? tournament.participantCount : (tournament.participantCount || 0)} ${this.$t('tournamentInfo.participants')}`
        },
        { 
          icon: 'fa-regular fa-trophy', 
          text: isLoading ? 
            this.$t('loading') : 
            `${hasDetails ? tournament.awardCount : (tournament.awardCount || 0)} ${this.$t('tournamentInfo.prizes')}`
        }
      ];
    },

    async init() {
      this.loading = true;
      try {
        await this.setTournamentArrays();
        this.atFilteredTournaments = this.getTournamentsFiltered("all", "actives", false);
        const response = await Repository.getTournamentsByClubId(this.$store.state.clubMember.club.id);
        if (response.status < 400)
          this.setClubTournaments(response.data);
        this.validateFeatureAvailable();
      } finally {
        this.loading = false;
        setTimeout(() => {
          this.updateSliderControlsStatus(document.querySelector('.club-tournaments-view .all-tournaments .cards-container-parent'));
          this.startObservingCards();
        }, 0);
      }
    },

    // New method to start observing all visible tournament cards
    startObservingCards() {
      this.atFilteredTournaments.forEach(tournament => {
        this.observeTournamentCard(tournament.id);
      });
    },

    validateFeatureAvailable() {
      let permissions = this.$store.state.clubPermissions;
      for (let permission of permissions) {
        if (permission.clubFeature.name == 'TOURNAMENT_CREATION') {
          this.isFeatureUnavailable = false;
          this.featurePermissionInfo = permission;
          break;
        }
      }
    },
    addTournament() {
      if (this.isFeatureUnavailable) {
        this.overlayUnavailableFeature = true;
      } else {
        this.openWizard();
      }
    },
    tournamentVisibility(tournament) {
      return tournament.tournamentVisibility === 'PUBLIC' ? this.$t('visibility.public') :
        tournament.tournamentVisibility === 'PRIVATE' ? this.$t('visibility.private') : this.$t('visibility.exclusive');
    },
    tournamentStatus(tournament) {
      let now = new Date();
      if (new Date(tournament.endTime) < now)
        return this.$t('status.finished');
      else if (new Date(tournament.startTime) > now)
        return this.$t('status.planning');
      else
        return this.$t('status.running');
    },
    tournamentStatusColor(tournament) {
      let now = new Date();
      if (new Date(tournament.endTime) < now)
        return '#D1D1D1';
      else if (new Date(tournament.startTime) > now)
        return '#FFF75F';
      else
        return '#00F784';
    },
    tournamentVisibilityColor(visibility) {
      switch (visibility) {
        case 'PUBLIC':
          return '#FFF75F';
        case 'PRIVATE':
          return '#66c3d0';
        default:
          return '#d1d1d1';
      }
    },
    goToTournament(tournament) {
      this.$router.push({
        name: 'tournament-overview',
        query: { tournamentId: Number.parseInt(tournament.id) }
      });
    },
    truncateString(string, maxLength) {
      return (string !== null && string.length > maxLength) ? `${string.substring(0, maxLength)}...` : string;
    },
    capitalize(string) {
      return string[0] + string.slice(1).toLowerCase();
    },
    openWizard() {
      this.wizardOpen = true;
      document.querySelector("html").classList.add("modal-open");
    },
    hideWizard() {
      this.wizardOpen = false;
      this.fromWizard = true;
      document.querySelector("html").classList.remove("modal-open");
    },
    async setTournamentArrays() {
      this.loading = true;
      this.allTournaments = this.$store.state.clubTournaments;
      this.userTournaments = await this.getTournamentsByUser();
      this.loading = false;
    },

    async getTournamentsByUser() {
      return (await tournament.getTournamentsByUser(this.$store.state.user.id)).data
    },

    async toggleArchivedView() {
      // Simply toggle the state - no need to load additional tournaments
      // The filteredTournaments computed property will handle showing/hiding archived ones
    },

    handleTournamentArchived(tournamentId) {
      // Remove tournament from current list
      this.atFilteredTournaments = this.atFilteredTournaments.filter(t => t.id !== tournamentId);
      this.allTournaments = this.allTournaments.filter(t => t.id !== tournamentId);
      
      // Update store
      const updatedTournaments = this.$store.state.clubTournaments.filter(t => t.id !== tournamentId);
      this.setClubTournaments(updatedTournaments);
      
      // Refresh tournaments to get updated data including the newly archived tournament
      //this.refreshTournaments();
    },

    // Add the missing showSnackbar method
    showSnackbar(messageData) {
      this.snackbarText = messageData.text;
      this.snackbarColor = messageData.color;
      this.snackbar = true;
    },

    // New method to refresh tournaments from the server
    async refreshTournaments() {
      try {
        const response = await Repository.getTournamentsByClubId(this.$store.state.clubMember.club.id);
        if (response.status < 400) {
          this.setClubTournaments(response.data);
          await this.setTournamentArrays();
          this.atFilteredTournaments = this.getTournamentsFiltered("all", this.atActiveTab, false);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error refreshing tournaments:', error);
      }
    },

    // Handle force refresh from tournament cards
    async handleForceRefresh() {
      await this.refreshTournaments();
    },

    getTournamentsFiltered(type, condition, onlyEnrolled) {
      let filteredTournaments = type == "all" ? this.allTournaments : this.allTournaments;
      filteredTournaments.forEach(t => {
        if (t.tournament) {
          Object.assign(t, {
            ...t.tournament,
            awardCount: t.awardCount,
            participantCount: t.participantCount,
          });
        }
      });

      let user_tournaments = this.userTournaments;

      filteredTournaments.forEach(t => {
        let tournament = user_tournaments.find(ut => ut.tournamentId == t.id);
        if (tournament) {
          let new_t = { ...t, ...tournament }
          new_t.enrolled = true;
          Object.assign(t, new_t)
        }
      });


      if (onlyEnrolled) {
        filteredTournaments = filteredTournaments.filter(ft => ft.lastUserRankingPlace !== undefined)
      }

      if (condition === "finished") {
        filteredTournaments = filteredTournaments
          .filter(t => (new Date(t.endTime) <= new Date()))
          .map(t => ({ ...t, finished: true })) // Mark as finished
          .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
      } else {
        // For active tournaments, filter out archived ones completely
        filteredTournaments = filteredTournaments.filter(t => {
          // Skip archived tournaments in active tab
          if (t.tournamentVisibility === 'ARCHIVED') {
            return false;
          }
          
          let startTime = new Date(t.startTime).getTime();
          let endTime = new Date(t.endTime).getTime();
          let now = new Date().getTime();
          t.inProgress = false;
          if (startTime < now && now < endTime) {
            t.inProgress = true;
            t.active = true;
          }
          else if (now < startTime && now < endTime) {
            t.active = true;
            t.inProgress = false;
          }
          if (t.active)
            return t;
        });
      }

      return filteredTournaments;
    },
    updateSliderControlsStatus(container) {
      let a = container.scrollLeft;
      let b = container.scrollWidth - container.clientWidth;
      //Scroll percentage (0-1)
      let c = a / b;
      if (c == 0) {
        container.parentElement.querySelector(".prev-btn").setAttribute("disabled", "disabled");
        container.parentElement.querySelector(".next-btn").removeAttribute("disabled");
      }
      else if (c == 1) {
        container.parentElement.querySelector(".next-btn").setAttribute("disabled", "disabled");
        container.parentElement.querySelector(".prev-btn").removeAttribute("disabled");
      } else if (isNaN(c)) {
        container.parentElement.querySelector(".next-btn").setAttribute("disabled", "disabled");
        container.parentElement.querySelector(".prev-btn").setAttribute("disabled", "disabled");
      } else {
        container.parentElement.querySelector(".next-btn").removeAttribute("disabled", "disabled");
        container.parentElement.querySelector(".prev-btn").removeAttribute("disabled", "disabled");
      }
    },
    scrollContainer(from, right) {
      let container = from == "at" ? document.querySelector(".all-tournaments__cards .cards-container-parent") : document.querySelector(".my-tournaments .cards-container-parent");

      if (window.innerWidth <= 768) {
        container.scrollBy(right ? 463 : -463, 0);
      } else {
        container.scrollBy(right ? 400 : -400, 0);
      }
    },
    async loadArchivedTournaments() {
      try {
        const response = await Repository.getArchivedTournaments();
        // Handle both successful response with data and 204 No Content
        if (response.status < 400) {
          // If 204 No Content or empty response, set empty array
          if (response.status === 204 || !response.data || !Array.isArray(response.data)) {
            this.archivedTournaments = [];
            return;
          }
          
          // Filter archived tournaments for current club
          this.archivedTournaments = response.data
            .filter(tournament => tournament.tournamentOrganizerId === this.$store.state.clubMember.club.id)
            .map(tournament => ({
              ...tournament,
              finished: true,
              active: false,
              inProgress: false,
              tournamentVisibility: 'ARCHIVED'
            }));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading archived tournaments:', error);
        
        // Only show error snackbar for actual errors, not 204 responses
        if (error.response && error.response.status !== 204) {
          this.showSnackbar({
            text: this.$t('errorArchivingTournament'),
            color: 'error'
          });
        }
        
        // Set empty array for any error case
        this.archivedTournaments = [];
      }
    },

    getMessageStatus(type, enrolled, active, inProgress, position, userStatus) {
      let msg = {};
      
      // Handle archived tournaments - check the tournament's visibility directly from the current tournament
      if (type === 'ALL') {
        // We need to find the current tournament being processed by matching its properties
        // Since we're in a loop rendering tournaments, we can't directly access the tournament object
        // Instead, we should pass the tournament visibility as a parameter or check it differently
        
        // For now, let's remove the faulty archived detection here
        // The archived status should be determined by the tournamentVisibility prop passed to TournamentCard
      }
      
      if (!active) {
        msg.text = this.$t('tournamentStatuses.closed');
        msg.color = "bg-gray";
      }
      switch (type) {
        case "ALL":
          if (active) {
            if (enrolled) {
              msg.text = this.$t('tournamentStatuses.alreadyIn');
              msg.color = "bg-black";
            }
            if (inProgress && !enrolled) {
              msg.text = this.$t('tournamentStatuses.missing');
              msg.color = "bg-gray";
            }
            if (!inProgress && !enrolled) {
              msg.text = this.$t('tournamentStatuses.joinNow');
              msg.color = "bg-black";
            }
          }
          break;
        default:
          if (!active) {
            msg.icon = "fa-regular fa-lock";
          } else {
            if (inProgress) {
              msg.text = this.$t('tournamentStatuses.active');
              msg.icon = "fa-regular fa-calendar-check";
              msg.color = "swt-success"
              msg.id = "MTA";
            }
            if (!inProgress) {
              msg.text = this.$t('tournamentStatuses.waiting');
              msg.icon = "fa-regular fa-clock";
              msg.id = "WTS";
            }
            if (inProgress && userStatus == "PAUSED") {
              msg.text = this.$t('tournamentStatuses.paused');
              msg.icon = "fa-regular fa-triangle-exclamation";
              msg.color = "swt-warning"
            }
            if (inProgress && userStatus == "DISQUALIFIED") {
              msg.text = this.$t('tournamentStatuses.disqualified');
              msg.icon = "fa-regular fa-circle-exclamation";
              msg.color = "swt-error"
            }
          }
          if (position <= 3 && position) {
            msg.text = `${active ? this.$t('tournamentStatuses.positionTexts.youre') : this.$t('tournamentStatuses.positionTexts.youfinished')} ${position}${position == 1 ? this.$t('tournamentStatuses.positionTexts.firstSuffix') : position == 2 ? this.$t('tournamentStatuses.positionTexts.secondSuffix') : this.$t('tournamentStatuses.positionTexts.thirdSuffix')} ${this.$t('tournamentStatuses.positionTexts.place')}`;
            msg.icon = "fa-regular fa-medal";
          }
          break;
      }
      return msg;

    },
    goToTournamentSettings(tournament) {
      this.$router.push({
        name: 'tournament-overview',
        query: { tournamentId: Number.parseInt(tournament.id), action: 'settings' }
      });
    },
    goToTournamentShare(tournament) {
      this.$router.push({
        name: 'tournament-overview',
        query: { tournamentId: Number.parseInt(tournament.id), action: 'share' }
      });
    },
    archiveTournamentPrompt(tournament) {
      this.archiveTarget = tournament;
      this.archiveAction = 'archive';
      this.showArchiveDialog = true;
    },
    unarchiveTournamentPrompt(tournament) {
      this.archiveTarget = tournament;
      this.archiveAction = 'unarchive';
      this.showArchiveDialog = true;
    },
    async confirmArchiveAction() {
      if (!this.archiveTarget) return;
      this.archiving = true;
      try {
        if (this.archiveAction === 'archive') {
          await Repository.archiveTournament(this.archiveTarget.id);
          this.handleTournamentArchived(this.archiveTarget.id);
          this.showSnackbar({ text: this.$t('tournamentArchived') || 'Competition archived', color: 'success' });
        } else {
          await Repository.unarchiveTournament(this.archiveTarget.id);
          this.handleTournamentArchived(this.archiveTarget.id);
          this.showSnackbar({ text: this.$t('tournamentUnarchived') || 'Competition unarchived', color: 'success' });
        }
        this.showArchiveDialog = false;
        await this.refreshTournaments();
      } catch (error) {
        this.showSnackbar({ text: this.$t('errorArchivingTournament') || 'Error updating competition', color: 'error' });
      } finally {
        this.archiving = false;
      }
    },
    getFormattedDate(date) {
      let year = date.getFullYear();
      let month = (1 + date.getMonth()).toString().padStart(2, '0');
      let day = date.getDate().toString().padStart(2, '0');

      return month + '/' + day + '/' + year;
    },
  }
}
</script>



<style lang="scss" scoped>
@import "src/global";

.theme--dark {
  .club-tournaments-view {
    .tournaments-heading {
      .tabs {
        background-color: #303740 !important;
      }
    }

    .tournaments-count-row {
      .view-toggle {
        border-color: #303740;

        button {
          color: #6b7280;

          &:not(.active):hover {
            background-color: #303740;
          }

          & + button {
            border-left-color: #303740;
          }
        }
      }
    }

    .all-tournaments__list {
      .competitions-table {
        tbody tr {
          &:hover {
            background-color: rgba(255, 255, 255, 0.03);
          }

          td {
            border-bottom-color: #303740;
          }
        }
      }

      .list-pagination {
        .pagination-pages button {
          border-color: #303740;

          &:not(.active):hover {
            background-color: #303740;
          }
        }

        .pagination-per-page select {
          border-color: #303740;
        }
      }
    }
  }
}

.club-tournaments-view {
  .all-tournaments {
    width: 100%;
  }

  .cards-container-parent {

    -ms-overflow-style: none;
    scrollbar-width: none;
    overflow: scroll;
    scroll-behavior: smooth;

    &::-webkit-scrollbar {
      display: none;
    }

    .cards-container {
      width: fit-content;
      display: flex;
      justify-content: flex-start;

      .tournament-card {
        margin-right: 50px;
        margin-bottom: 20px;

        @media (max-width: 768px) {
          width: 89vw;
          margin-right: 10px;
          margin-left: 10px;
        }
      }
    }
  }

  .tournaments-heading {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 27px;

    .tournaments-heading__items {
      display: flex;
      align-items: center;
      width: 100%;
      gap: 10px;
      flex-wrap: wrap;

      .create-btn {
        color: #ffffff !important;
        font-family: "Aeonik", "Avenir", sans-serif;
        font-size: 16px;
        font-weight: 500;
        text-transform: none;
        border-radius: 8px;
        letter-spacing: 0;
        height: 40px;
        padding: 0 16px !important;
        box-shadow: none;
        white-space: nowrap;
        margin-left: auto;
        flex-shrink: 0;
      }

      .tabs {
        display: flex;
        position: relative;
        background-color: #ebebeb;
        border-radius: 10px;

        * {
          z-index: 2;
        }

        input[type="radio"] {
          &:checked {
            &+label {
              color: var(--tab-active-text-color) !important;
            }
          }
        }

        input[type="radio"] {
          display: none;
        }

        input[id="active-option"] {
          &:checked {
            &~.glider {
              transform: translateX(0);
            }
          }
        }

        label {
          i {
            padding-right: 10px;
            font-size: 13px;
          }
        }

        input[id="finished-option"] {
          &:checked {
            &~.glider {
              transform: translateX(100%);
            }
          }
        }

        input[id="archived-option"] {
          &:checked {
            &~.glider {
              transform: translateX(200%);
            }
          }
        }

        .tab {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 160px;
          height: 38px;
          font-size: 14px;
          font-weight: normal;
          color: var(--v-mainBlack-base);
          border-radius: 10px;
          cursor: pointer;
          transition: color 0.15s ease-in;
          font-family: "Avenir", sans-serif;
          white-space: nowrap;
          padding: 0 12px;
        }

        .glider {
          position: absolute;
          display: flex;
          height: 38px;
          width: 160px;
          background-color: var(--club-color);
          border-radius: 10px;
          z-index: 1;
          transition: 0.25s ease-out;
        }

      }
    }

    .all-tournaments-heading__add-button {
      display: flex;
      align-items: center;
      padding: 10px;
      border-radius: 30px;
      background: transparent;
      border: 1px solid var(--v-mainGreen-base);
      color: var(--v-mainGreen-base);
      font-family: "Avenir", sans-serif;
      font-size: 16px;
      font-weight: normal;
      transition: box-shadow ease-in-out 0.1s;

      i {
        padding-right: 10px;
      }

      &:hover {
        box-shadow: 0px 0px 6px 0px rgba(0, 195, 97, 1);
      }
    }
  }

  .slider-controls {
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: flex-end;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      outline: 0;
      margin: 5px 10px;
      width: 38px;
      height: 38px;
      background: var(--club-color);
      padding: 0;
      border-radius: 8px;

      i {
        color: var(--v-mainWhite-base);
      }

      &:disabled {
        background: var(--v-mainBlack-base);
        opacity: 0.1;

        i {
          color: var(--v-mainWhite-base);
        }
      }
    }
  }

  .all-tournaments__cards__noTournaments-paragraph {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    font-family: "Avenir", sans-serif;
    text-align: center;
    height: 320px;
    color: var(--v-mainBlack-base);
    opacity: 0.2;
  }

  .primary--text {
    color: var(--club-color) !important;
    caret-color: var(--club-color) !important;
  }

  .tournaments-search {
    margin-bottom: 16px;

    .search-container {
      max-width: 350px;
    }
  }

  .tournaments-count-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    .competitions-count {
      font-family: "Avenir", sans-serif;
      font-size: 14px;
      color: #ababab !important;
      font-weight: 400;
    }

    .view-toggle {
      display: flex;
      border: 1px solid #ebebeb;
      border-radius: 8px;
      overflow: hidden;

      button {
        background: transparent;
        border: none;
        padding: 8px 12px;
        cursor: pointer;
        color: #ababab;
        font-size: 16px;
        transition: all 0.15s ease;

        &.active {
          background-color: var(--club-color);
          color: #ffffff;
        }

        &:not(.active):hover {
          background-color: #f5f5f5;
        }

        & + button {
          border-left: 1px solid #ebebeb;
        }
      }
    }
  }

  .all-tournaments__list {
    width: 100%;

    .competitions-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      font-family: "Avenir", sans-serif;

      thead {
        tr {
          th {
            background-color: var(--club-color);
            color: #ffffff;
            font-weight: 500;
            font-size: 14px;
            padding: 12px 16px;
            text-align: left;
            white-space: nowrap;

            &.th-center {
              text-align: center;
            }

            &:first-child {
              border-radius: 8px 0 0 8px;
            }

            &:last-child {
              border-radius: 0 8px 8px 0;
            }
          }
        }
      }

      tbody {
        tr {
          cursor: pointer;
          transition: background-color 0.15s ease;

          &:hover {
            background-color: rgba(0, 0, 0, 0.03);
          }

          td {
            padding: 14px 16px;
            font-size: 14px;
            border-bottom: 1px solid #ebebeb;
            color: var(--v-mainBlack-base);
          }

          .td-name,
          .td-date {
            color: var(--v-mainBlack-base);
            font-weight: normal;
          }

          .td-participants,
          .td-prizes {
            color: var(--v-mainBlack-base);
            font-weight: normal;
            text-align: center;
          }

          .td-actions {
            text-align: center;

            .action-icon {
              color: #ababab;
              font-size: 16px;
              cursor: pointer;
              padding: 6px;
              transition: color 0.15s ease;

              &:hover {
                color: var(--v-mainBlack-base);
              }

              & + .action-icon {
                margin-left: 8px;
              }
            }
          }

          .td-empty {
            text-align: center;
            color: #ababab;
            padding: 40px 16px;
          }
        }
      }
    }

    .list-pagination {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 20px;
      margin-top: 16px;
      font-family: "Avenir", sans-serif;
      font-size: 14px;

      .pagination-info {
        color: #ababab;
      }

      .pagination-pages {
        display: flex;
        gap: 6px;

        button {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1px solid #ebebeb;
          background: transparent;
          cursor: pointer;
          font-size: 13px;
          color: var(--v-mainBlack-base);
          transition: all 0.15s ease;

          &.active {
            background-color: var(--club-color);
            border-color: var(--club-color);
            color: var(--club-contrast, #ffffff);
          }

          &:not(.active):hover {
            background-color: #f5f5f5;
          }
        }
      }

      .pagination-per-page {
        select {
          border: 1px solid #ebebeb;
          border-radius: 6px;
          padding: 4px 8px;
          font-size: 13px;
          background: transparent;
          color: var(--v-mainBlack-base);
          cursor: pointer;
        }
      }
    }
  }
}

.button-add {
  background-color: #111111 !important;
  color: #ffffff !important;
  border: #111111 solid 1px;
}

.tournament-head {
  color: $black-color;
  font-size: 25px;
}


.search-container {
  min-width: 150px;

  .search-input {
    font-family: 'Avenir', sans-serif;

    ::v-deep .v-input__slot {
      background-color: var(--v-mainWhite-base) !important;
    }

    ::v-deep .v-input__prepend-inner i {
      color: var(--club-color) !important;
    }

    /* Active/Focus states */
    ::v-deep .v-input--is-focused {
      .v-input__slot {
        border-color: var(--club-color) !important;
        caret-color: var(--club-color) !important;
      }

      .v-input__prepend-inner i,
      .v-label--active {
        color: var(--club-color) !important;
      }
    }

    /* Clear icon color */
    ::v-deep .v-input__icon--clear .v-icon {
      color: var(--club-color) !important;
      border: none;

      &:hover {
        color: var(--club-color) !important;
        background-color: transparent;
      }
    }

    /* Selection color */
    ::v-deep .v-input__slot::selection {
      background-color: var(--club-color) !important;
      opacity: 0.2;
    }
  }
}

.tournaments-wizard {
  position: fixed;
  padding-left: 120px;

  @media (max-width: 768px) {
    padding-left: 30px;
    ;
  }

  width: 100%;
  top: 0;
  left: 0;
  transform: scale(0);
  z-index: 140 !important;
  background: var(--v-mainWhite-base);
  overflow-y: auto;

  &.three {
    z-index: 0;
    transform: scale(1);
    animation: moveUp 0.7s cubic-bezier(0.165, 0.840, 0.440, 1.000) forwards;

    +.content {
      z-index: 1;
      animation: slideUpLarge 0.7s cubic-bezier(0.165, 0.840, 0.440, 1.000) forwards;
    }

    &.out {

      transform: scale(0);
      animation: moveDown 0.7s cubic-bezier(0.165, 0.840, 0.440, 1.000) forwards;

      +.content {
        animation: slideDownLarge 0.7s cubic-bezier(0.165, 0.840, 0.440, 1.000) forwards;
      }
    }
  }
}

@keyframes slideUpLarge {
  0% {
    transform: translateY(0%);
  }

  100% {
    transform: translateY(-100%);
  }
}

@keyframes slideDownLarge {
  0% {
    transform: translateY(-100%);
  }

  100% {
    transform: translateY(0%);
  }
}

@keyframes moveUp {
  0% {
    transform: translateY(100vh);
  }

  100% {
    transform: translateY(0);
  }
}

@keyframes moveDown {
  0% {
    transform: translateY(0px);
  }

  100% {
    transform: translateY(100vh);
  }
}

::v-deep .v-input--switch {
    .v-input--selection-controls__input {
        .v-input--switch__track {
            height: 24px !important;
            width: 45px !important;
            top: calc(50% - 12px) !important;
            border-radius: 12px !important;
        }

        .v-input--switch__thumb {
            height: 20px !important;
            margin-left: 5px;
            width: 20px !important;
            top: calc(50% - 10px) !important;
        }
    }

    // Adjust margins for label alignment
    .v-input--selection-controls__input {
        margin-right: 18px;
    }

    // Adjust thumb position when switched on
    &.v-input--is-active {
        .v-input--switch__thumb {
            transform: translate(20px) !important;
        }

        // Add green color when active
        .v-input--switch__track {
            background-color: var(--v-mainGreen-base) !important;
            opacity: 1 !important;
        }
    }

    .v-input--selection-controls__label {
        color: var(--v-mainBlack-base) !important;
    }

    .v-label.theme--light {
        color: var(--v-mainBlack-base) !important;
    }
}
</style>
